import type { Locale } from '@xdocs/i18n';

/**
 * STOP-GAP local catalog for the `admin:` scope (F53-F61/F68/F71 client),
 * following the exact `documents:`/`search:`/`sharing:` precedent
 * (`apps/web/src/features/search/search.i18n.ts`) — NOT a second i18n
 * runtime (E18/§13.3 ban), just physically scoped here because
 * `packages/i18n/**` is outside this task's ownership boundary.
 *
 * OPEN ISSUE: fold into `packages/i18n/src/catalogs/{en,ro}/admin.json`.
 */
const en = {
  'shell.title': 'Admin console',
  'shell.backToApp': 'Back to app',
  'shell.brandBadge': 'CONSOLE',
  'shell.navGroup': 'SURFACES',
  'shell.envProd': 'PROD',
  'shell.envDev': 'DEV',
  'shell.roleAdmin': 'ADMIN',
  'shell.roleModerator': 'MODERATOR',
  'nav.overview': 'Overview',
  'nav.users': 'Users',
  'nav.invoices': 'Invoices',
  'nav.billing': 'Billing',
  'nav.analytics': 'Analytics',
  'nav.inbox': 'Contact / DMCA',
  'nav.assets': 'Assets / moderation',
  'nav.reviewQueue': 'Review queue',
  'nav.promoCodes': 'Promo codes',
  'nav.audit': 'Audit log',
  'nav.landingCms': 'Landing CMS',

  'forbidden.title': "You don't have access to this",
  'forbidden.description': 'This section is restricted to staff accounts. If you think this is a mistake, contact an administrator.',
  'forbidden.cta': 'Back to app',
  'roleGate.error.title': "Couldn't check your access.",

  'chart.column.category': 'Category',
  'chart.column.period': 'Period',
  'chart.column.value': 'Value',

  'role.user': 'user',
  'role.moderator': 'moderator',
  'role.admin': 'admin',
  'tier.free': 'free',
  'tier.pro': 'pro',
  'status.active': 'active',
  'status.suspended': 'suspended',
  'status.deleted': 'deleted',

  'impersonation.banner.viewingAs': 'Viewing as {name} (staff session)',
  'impersonation.banner.endsIn': 'ends in {time}',
  'impersonation.banner.exit': 'Exit view-as',
  'impersonation.toast.entered': 'View-as session started for {name} — see the banner below.',
  'impersonation.toast.exited': 'Exited view-as session.',
  'impersonation.toast.failed': "Couldn't start view-as — try again.",
  'impersonation.confirm.title': 'Enter view-as for “{email}”',
  'impersonation.confirm.body':
    'Opens the app in a time-boxed, read-mostly impersonated session. Writes are blocked except a reviewed whitelist. Audited as impersonation.start.',
  'impersonation.confirm.typeToConfirmLabel': 'Type the email to confirm',
  'impersonation.confirm.typeToConfirmHint': 'Type “{email}” exactly to enable this action.',
  'impersonation.confirm.confirmLabel': 'Enter view-as',
  'impersonation.confirm.reasonLabel': 'Reason (for the audit log)',
  'impersonation.confirm.reasonRequired': 'A reason is required.',

  'overview.title': 'Platform overview',
  'overview.subtitle': 'Cached read-models · last updated {time}.',
  'overview.kpi.signups': 'Signups',
  'overview.kpi.dau': 'DAU',
  'overview.kpi.wau': 'WAU',
  'overview.kpi.mau': 'MAU',
  'overview.kpi.creditSpend': 'Credit spend',
  'overview.kpi.creditEarn': 'Credit earn',
  'overview.chart.title': 'Generation volume · last 12 weeks',
  'overview.chart.ariaLabel': 'Generation volume by week, last 12 weeks',
  'overview.chart.empty': 'No generation activity in the last 12 weeks yet.',
  'overview.error.title': "Couldn't load the platform overview.",
  'overview.empty.title': 'No analytics data yet',
  'overview.empty.description': 'Read-models populate as the platform gets usage.',

  'users.title': 'Users',
  'users.count': '{count, plural, one {# user} other {# users}}',
  'users.search.label': 'Search users',
  'users.search.placeholder': 'Search users…',
  'users.filter.role': 'Role',
  'users.filter.roleAll': 'All roles',
  'users.filter.tier': 'Tier',
  'users.filter.tierAll': 'All tiers',
  'users.filter.status': 'Status',
  'users.filter.statusAll': 'All statuses',
  'users.column.email': 'Email',
  'users.column.role': 'Role',
  'users.column.tier': 'Tier',
  'users.column.credits': 'Credits',
  'users.column.status': 'Status',
  'users.column.joined': 'Joined',
  'users.empty.title': 'No users yet',
  'users.empty.description': 'Users appear here once people sign up.',
  'users.noResults.title': 'No users match “{query}”.',
  'users.error.title': "Couldn't load users.",

  'userDetail.back': 'Users',
  // No `docs` count — `AdminUserDetail` (`packages/schemas/src/admin.ts`)
  // doesn't carry a document count for the target user (see WIRING.md).
  'userDetail.meta': '{email} · joined {date}',
  'userDetail.liveNotice': 'Tier & credit changes reflect live to the user immediately.',
  'userDetail.tabs.profile': 'Profile',
  'userDetail.tabs.credits': 'Credits',
  'userDetail.tabs.sessions': 'Sessions',
  'userDetail.tabs.subscription': 'Subscription',
  'userDetail.tabs.activity': 'Activity',
  'userDetail.error.title': "Couldn't load this user.",

  'userDetail.profile.eyebrow': 'Sensitive actions · confirm + reason → audited',
  'userDetail.action.setRole.label': 'Set platform role',
  'userDetail.action.setRole.sub': 'user · moderator · admin',
  'userDetail.action.changeTier.label': 'Change tier',
  'userDetail.action.changeTier.sub': 'billing state',
  'userDetail.action.adjustCredits.label': 'Adjust credits',
  'userDetail.action.adjustCredits.sub': 'grant / deduct',
  'userDetail.action.forceLogout.label': 'Force-logout sessions',
  'userDetail.action.forceLogout.sub': 'all devices',
  'userDetail.action.suspend.label': 'Suspend account',
  'userDetail.action.reinstate.label': 'Reinstate account',
  'userDetail.action.suspend.sub': 'blocks all AI actions',
  'userDetail.action.impersonate.label': 'Enter view-as',
  'userDetail.action.impersonate.sub': 'F58 impersonation',

  'userDetail.confirm.setRole.title': 'Change role for “{email}”',
  'userDetail.confirm.setRole.body': 'Platform RBAC — distinct from per-document share roles. Writes users.role and is audited as user.role_change.',
  'userDetail.confirm.roleSelectLabel': 'New role',
  'userDetail.confirm.changeTier.title': 'Change tier for “{email}”',
  'userDetail.confirm.changeTier.body': 'Billing state, orthogonal to role. Reconciles against Paddle.',
  'userDetail.confirm.tierSelectLabel': 'New tier',
  'userDetail.confirm.adjustCredits.title': 'Adjust credits for “{email}”',
  'userDetail.confirm.adjustCredits.body': 'Writes a credit_ledger row (admin_grant). Balance {balance} credits. Reflects live to the user.',
  'userDetail.confirm.creditsDeltaLabel': 'Amount (negative to deduct)',
  'userDetail.confirm.creditsDeltaRequired': 'Enter a non-zero amount.',
  'userDetail.confirm.forceLogout.title': 'Force-logout “{email}”',
  'userDetail.confirm.forceLogout.body': 'Revokes active sessions across all devices. The user will be signed out immediately.',
  'userDetail.confirm.suspend.title': 'Suspend “{email}”',
  'userDetail.confirm.suspend.body': 'Signs them out and blocks all AI actions. Status active → suspended. Recorded in the audit log.',
  'userDetail.confirm.reinstate.title': 'Reinstate “{email}”',
  'userDetail.confirm.reinstate.body': 'Restores access. Recorded in the audit log.',
  'userDetail.confirm.reasonLabel': 'Reason (for the audit log)',
  'userDetail.confirm.reasonRequired': 'A reason is required.',
  'userDetail.confirm.confirm': 'Confirm',

  'userDetail.toast.roleUpdated': 'Role updated for {email}.',
  'userDetail.toast.tierUpdated': 'Tier updated for {email}.',
  'userDetail.toast.creditsAdjusted': 'Credits adjusted for {email}.',
  'userDetail.toast.sessionsRevoked': 'Sessions revoked for {email}.',
  'userDetail.toast.suspended': 'Suspended {email}.',
  'userDetail.toast.reinstated': 'Reinstated {email}.',
  'userDetail.toast.actionFailed': "Couldn't complete this action — try again.",

  'userDetail.credits.label': 'credits',
  'userDetail.credits.historyUnavailable': "This view shows the current balance. Full transaction history isn't wired to an admin-scoped op yet.",

  'userDetail.sessions.activeCount': '{count, plural, one {# active session} other {# active sessions}}',
  'userDetail.sessions.description': 'Per-device session detail isn’t exposed to admins yet — use Force-logout to revoke every session for this user.',

  'userDetail.subscription.none': 'Free — no active subscription.',
  'userDetail.subscription.renews': 'Renews {date}',
  'userDetail.subscription.cancelAt': 'Cancels at period end ({date})',
  'userDetail.subscription.status': 'Status: {status}',

  'userDetail.activity.empty.title': 'No activity yet',
  'userDetail.activity.empty.description': 'Actions this user performs will show up here.',
  'userDetail.activity.error.title': "Couldn't load this user's activity.",
  'userDetail.activity.noResults.title': 'No matching activity.',
  'userDetail.activity.column.action': 'Action',
  'userDetail.activity.column.target': 'Target',
  'userDetail.activity.column.when': 'When',

  'analytics.title': 'Analytics',
  'analytics.kpi.dau': 'DAU',
  'analytics.kpi.wau': 'WAU',
  'analytics.kpi.mau': 'MAU',
  'analytics.kpi.genSuccess': 'Gen success',
  // mockup-43: mock's chart card heading carries a "· 12 months" range qualifier.
  'analytics.chart.byWorker.title': 'Generation jobs by worker · 12 months',
  'analytics.chart.byWorker.ariaLabel': 'Successful generation jobs by worker type',
  'analytics.conversion.title': 'Conversion',
  'analytics.conversion.freeToPro': 'Free → PRO',
  'analytics.conversion.signupToFirstDoc': 'Signup → first document',
  'analytics.topAssets.title': 'Top public assets',
  'analytics.topAssets.empty': 'No public assets yet.',
  'analytics.topAssets.views': '{count, plural, one {# view} other {# views}}',
  'analytics.error.title': "Couldn't load analytics.",
  'analytics.empty.title': 'No analytics data yet',
  'analytics.empty.description': 'Read-models populate as the platform gets usage.',
} as const;

const ro: Record<keyof typeof en, string> = {
  'shell.title': 'Consolă admin',
  'shell.backToApp': 'Înapoi în aplicație',
  'shell.brandBadge': 'CONSOLE',
  'shell.navGroup': 'SUPRAFEȚE',
  'shell.envProd': 'PROD',
  'shell.envDev': 'DEV',
  'shell.roleAdmin': 'ADMIN',
  'shell.roleModerator': 'MODERATOR',
  'nav.overview': 'Prezentare generală',
  'nav.users': 'Utilizatori',
  'nav.invoices': 'Facturi',
  'nav.billing': 'Facturare',
  'nav.analytics': 'Analitice',
  'nav.inbox': 'Contact / DMCA',
  'nav.assets': 'Materiale / moderare',
  'nav.reviewQueue': 'Coadă de revizuire',
  'nav.promoCodes': 'Coduri promo',
  'nav.audit': 'Jurnal de audit',
  'nav.landingCms': 'CMS pagină principală',

  'forbidden.title': 'Nu ai acces la această secțiune',
  'forbidden.description': 'Această secțiune este restricționată echipei. Dacă crezi că este o greșeală, contactează un administrator.',
  'forbidden.cta': 'Înapoi în aplicație',
  'roleGate.error.title': 'Nu am putut verifica accesul tău.',

  'chart.column.category': 'Categorie',
  'chart.column.period': 'Perioadă',
  'chart.column.value': 'Valoare',

  'role.user': 'utilizator',
  'role.moderator': 'moderator',
  'role.admin': 'admin',
  'tier.free': 'free',
  'tier.pro': 'pro',
  'status.active': 'activ',
  'status.suspended': 'suspendat',
  'status.deleted': 'șters',

  'impersonation.banner.viewingAs': 'Vizualizezi ca {name} (sesiune staff)',
  'impersonation.banner.endsIn': 'se termină în {time}',
  'impersonation.banner.exit': 'Ieși din view-as',
  'impersonation.toast.entered': 'Sesiunea view-as a pornit pentru {name} — vezi bannerul de mai jos.',
  'impersonation.toast.exited': 'Ai ieșit din sesiunea view-as.',
  'impersonation.toast.failed': 'Nu am putut porni view-as — încearcă din nou.',
  'impersonation.confirm.title': 'Intră în view-as pentru „{email}”',
  'impersonation.confirm.body':
    'Deschide aplicația într-o sesiune impersonată, limitată în timp, doar-citire. Scrierile sunt blocate cu excepția unei liste aprobate. Înregistrat ca impersonation.start.',
  'impersonation.confirm.typeToConfirmLabel': 'Scrie emailul pentru a confirma',
  'impersonation.confirm.typeToConfirmHint': 'Scrie exact „{email}” pentru a activa această acțiune.',
  'impersonation.confirm.confirmLabel': 'Intră în view-as',
  'impersonation.confirm.reasonLabel': 'Motiv (pentru jurnalul de audit)',
  'impersonation.confirm.reasonRequired': 'Motivul este obligatoriu.',

  'overview.title': 'Prezentare generală platformă',
  'overview.subtitle': 'Modele de citire în cache · actualizat {time}.',
  'overview.kpi.signups': 'Înscrieri',
  'overview.kpi.dau': 'DAU',
  'overview.kpi.wau': 'WAU',
  'overview.kpi.mau': 'MAU',
  'overview.kpi.creditSpend': 'Credite cheltuite',
  'overview.kpi.creditEarn': 'Credite câștigate',
  'overview.chart.title': 'Volum de generare · ultimele 12 săptămâni',
  'overview.chart.ariaLabel': 'Volum de generare pe săptămână, ultimele 12 săptămâni',
  'overview.chart.empty': 'Nicio activitate de generare în ultimele 12 săptămâni încă.',
  'overview.error.title': 'Nu am putut încărca prezentarea generală.',
  'overview.empty.title': 'Încă nu există date de analiză',
  'overview.empty.description': 'Modelele de citire se populează pe măsură ce platforma este folosită.',

  'users.title': 'Utilizatori',
  'users.count': '{count, plural, one {# utilizator} few {# utilizatori} other {# de utilizatori}}',
  'users.search.label': 'Caută utilizatori',
  'users.search.placeholder': 'Caută utilizatori…',
  'users.filter.role': 'Rol',
  'users.filter.roleAll': 'Toate rolurile',
  'users.filter.tier': 'Nivel',
  'users.filter.tierAll': 'Toate nivelurile',
  'users.filter.status': 'Stare',
  'users.filter.statusAll': 'Toate stările',
  'users.column.email': 'Email',
  'users.column.role': 'Rol',
  'users.column.tier': 'Nivel',
  'users.column.credits': 'Credite',
  'users.column.status': 'Stare',
  'users.column.joined': 'Înscris',
  'users.empty.title': 'Încă nu sunt utilizatori',
  'users.empty.description': 'Utilizatorii apar aici după ce se înscriu.',
  'users.noResults.title': 'Niciun utilizator nu se potrivește cu „{query}”.',
  'users.error.title': 'Nu am putut încărca utilizatorii.',

  'userDetail.back': 'Utilizatori',
  'userDetail.meta': '{email} · înscris {date}',
  'userDetail.liveNotice': 'Modificările de nivel și credite se reflectă live către utilizator, imediat.',
  'userDetail.tabs.profile': 'Profil',
  'userDetail.tabs.credits': 'Credite',
  'userDetail.tabs.sessions': 'Sesiuni',
  'userDetail.tabs.subscription': 'Abonament',
  'userDetail.tabs.activity': 'Activitate',
  'userDetail.error.title': 'Nu am putut încărca acest utilizator.',

  'userDetail.profile.eyebrow': 'Acțiuni sensibile · confirmare + motiv → înregistrate',
  'userDetail.action.setRole.label': 'Setează rol platformă',
  'userDetail.action.setRole.sub': 'utilizator · moderator · admin',
  'userDetail.action.changeTier.label': 'Schimbă nivelul',
  'userDetail.action.changeTier.sub': 'stare de facturare',
  'userDetail.action.adjustCredits.label': 'Ajustează credite',
  'userDetail.action.adjustCredits.sub': 'acordă / dedu',
  'userDetail.action.forceLogout.label': 'Deconectează forțat sesiunile',
  'userDetail.action.forceLogout.sub': 'toate dispozitivele',
  'userDetail.action.suspend.label': 'Suspendă contul',
  'userDetail.action.reinstate.label': 'Reactivează contul',
  'userDetail.action.suspend.sub': 'blochează toate acțiunile AI',
  'userDetail.action.impersonate.label': 'Intră în view-as',
  'userDetail.action.impersonate.sub': 'F58 impersonare',

  'userDetail.confirm.setRole.title': 'Schimbă rolul pentru „{email}”',
  'userDetail.confirm.setRole.body': 'RBAC la nivel de platformă — distinct de rolurile per document. Scrie users.role și este înregistrat ca user.role_change.',
  'userDetail.confirm.roleSelectLabel': 'Rol nou',
  'userDetail.confirm.changeTier.title': 'Schimbă nivelul pentru „{email}”',
  'userDetail.confirm.changeTier.body': 'Stare de facturare, independentă de rol. Se reconciliază cu Paddle.',
  'userDetail.confirm.tierSelectLabel': 'Nivel nou',
  'userDetail.confirm.adjustCredits.title': 'Ajustează creditele pentru „{email}”',
  'userDetail.confirm.adjustCredits.body': 'Scrie un rând credit_ledger (admin_grant). Sold {balance} credite. Se reflectă live către utilizator.',
  'userDetail.confirm.creditsDeltaLabel': 'Sumă (negativă pentru deducere)',
  'userDetail.confirm.creditsDeltaRequired': 'Introdu o sumă diferită de zero.',
  'userDetail.confirm.forceLogout.title': 'Deconectează forțat „{email}”',
  'userDetail.confirm.forceLogout.body': 'Revocă sesiunile active pe toate dispozitivele. Utilizatorul va fi deconectat imediat.',
  'userDetail.confirm.suspend.title': 'Suspendă „{email}”',
  'userDetail.confirm.suspend.body': 'Îl deconectează și blochează toate acțiunile AI. Stare activ → suspendat. Înregistrat în jurnalul de audit.',
  'userDetail.confirm.reinstate.title': 'Reactivează „{email}”',
  'userDetail.confirm.reinstate.body': 'Restaurează accesul. Înregistrat în jurnalul de audit.',
  'userDetail.confirm.reasonLabel': 'Motiv (pentru jurnalul de audit)',
  'userDetail.confirm.reasonRequired': 'Motivul este obligatoriu.',
  'userDetail.confirm.confirm': 'Confirmă',

  'userDetail.toast.roleUpdated': 'Rol actualizat pentru {email}.',
  'userDetail.toast.tierUpdated': 'Nivel actualizat pentru {email}.',
  'userDetail.toast.creditsAdjusted': 'Credite ajustate pentru {email}.',
  'userDetail.toast.sessionsRevoked': 'Sesiuni revocate pentru {email}.',
  'userDetail.toast.suspended': 'Suspendat {email}.',
  'userDetail.toast.reinstated': 'Reactivat {email}.',
  'userDetail.toast.actionFailed': 'Nu am putut finaliza acțiunea — încearcă din nou.',

  'userDetail.credits.label': 'credite',
  'userDetail.credits.historyUnavailable': 'Această vizualizare arată soldul curent. Istoricul complet nu este încă disponibil printr-o operațiune dedicată admin.',

  'userDetail.sessions.activeCount': '{count, plural, one {# sesiune activă} few {# sesiuni active} other {# de sesiuni active}}',
  'userDetail.sessions.description': 'Detaliile per dispozitiv nu sunt încă expuse adminilor — folosește Deconectare forțată pentru a revoca toate sesiunile acestui utilizator.',

  'userDetail.subscription.none': 'Free — niciun abonament activ.',
  'userDetail.subscription.renews': 'Se reînnoiește {date}',
  'userDetail.subscription.cancelAt': 'Se anulează la finalul perioadei ({date})',
  'userDetail.subscription.status': 'Stare: {status}',

  'userDetail.activity.empty.title': 'Încă nu există activitate',
  'userDetail.activity.empty.description': 'Acțiunile acestui utilizator vor apărea aici.',
  'userDetail.activity.error.title': 'Nu am putut încărca activitatea acestui utilizator.',
  'userDetail.activity.noResults.title': 'Nicio activitate potrivită.',
  'userDetail.activity.column.action': 'Acțiune',
  'userDetail.activity.column.target': 'Țintă',
  'userDetail.activity.column.when': 'Când',

  'analytics.title': 'Analitice',
  'analytics.kpi.dau': 'DAU',
  'analytics.kpi.wau': 'WAU',
  'analytics.kpi.mau': 'MAU',
  'analytics.kpi.genSuccess': 'Succes generare',
  'analytics.chart.byWorker.title': 'Joburi de generare pe worker · 12 luni',
  'analytics.chart.byWorker.ariaLabel': 'Joburi de generare reușite, pe tip de worker',
  'analytics.conversion.title': 'Conversie',
  'analytics.conversion.freeToPro': 'Free → PRO',
  'analytics.conversion.signupToFirstDoc': 'Înscriere → primul document',
  'analytics.topAssets.title': 'Cele mai populare materiale publice',
  'analytics.topAssets.empty': 'Încă nu există materiale publice.',
  'analytics.topAssets.views': '{count, plural, one {# vizualizare} few {# vizualizări} other {# de vizualizări}}',
  'analytics.error.title': 'Nu am putut încărca analiticele.',
  'analytics.empty.title': 'Încă nu există date de analiză',
  'analytics.empty.description': 'Modelele de citire se populează pe măsură ce platforma este folosită.',
};

export const ADMIN_CATALOG: Record<Locale, Record<keyof typeof en, string>> = { en, ro };

export type AdminCatalogKey = keyof typeof en;
